/**
 * How a monitoring number, timestamp or date is written on screen.
 *
 * Both monitoring pages (the template page here, the run page in DOP-452)
 * render the same ledger vocabulary, so the formatting lives in one place
 * rather than being re-derived per component: a figure written "4.85x" on the
 * board and "4.9x" on the run page would read as two different numbers.
 *
 * Units come from the contract (`MonitoringUnit`): `usd_m` is USD millions and
 * carries no symbol or suffix — the column header says the unit, and a "$130.0M"
 * inside a covenant calculation is noise; `x` is a multiple; `pct` is a
 * FRACTION on the wire (0.083) written as a percentage.
 */

import type { MonitoringUnit } from "../../types/monitoring";

/** Decimals per unit — the precision the compliance certificates are stated to. */
const DECIMALS: Record<MonitoringUnit, number> = {
	usd_m: 1,
	x: 2,
	pct: 1,
};

/** What an absent number renders as. Never an empty cell: blank reads as zero. */
export const EMPTY_FIGURE = "—";

/**
 * A ledger value in its unit. `null`/`undefined` (a gap, or a figure nobody
 * has entered) is the em dash, and so is a non-finite number — `NaN` reaching
 * a cell is a bug upstream, and "NaN" on screen helps nobody read the row.
 */
export function formatFigure(
	value: number | null | undefined,
	unit: MonitoringUnit,
): string {
	if (value === null || value === undefined || !Number.isFinite(value)) {
		return EMPTY_FIGURE;
	}
	const decimals = DECIMALS[unit];
	if (unit === "x") return `${value.toFixed(decimals)}x`;
	if (unit === "pct") return `${(value * 100).toFixed(decimals)}%`;
	return value.toFixed(decimals);
}

/**
 * An epoch-millisecond timestamp as a local date and time. The contract sends
 * every timestamp as epoch ms, so this is the only place that decides what a
 * "generated at" looks like.
 */
export function formatEpochMs(value: number | null | undefined): string {
	if (value === null || value === undefined || !Number.isFinite(value)) {
		return EMPTY_FIGURE;
	}
	return new Date(value).toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

/**
 * An ISO `YYYY-MM-DD` date as a readable one.
 *
 * Parsed by hand rather than through `new Date(iso)`: the Date constructor
 * reads a bare ISO date as UTC midnight, which renders as the PREVIOUS day for
 * every viewer west of Greenwich — a package due date that slips a day is a
 * wrong date, not a cosmetic one.
 */
export function formatIsoDate(value: string | null | undefined): string {
	if (!value) return EMPTY_FIGURE;
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!match) return value;
	const [, year, month, day] = match;
	const local = new Date(Number(year), Number(month) - 1, Number(day));
	if (Number.isNaN(local.getTime())) return value;
	return local.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}
